/**
 * <kik-cart-trust> — preserves the cart-drawer trust-badge row across AJAX re-renders.
 *
 * The cart-drawer lives in a section group (overlay-group.json). Shopify's Section
 * Rendering API does not include section.blocks for section-group sections, so every
 * /cart/change.js or /?sections=cart-drawer response renders the section WITHOUT the
 * trust-badge <ul>. Each cart mutation that swaps the drawer's content would otherwise
 * wipe the badges from the DOM.
 *
 * Template source: <template id="kik-cart-trust-template"> at the top of the
 * cart-drawer section (always rendered, outside the cart-empty conditional) so the
 * badge markup is captured on the very first page load — even when the cart starts
 * empty and no live <kik-cart-trust> exists in the footer yet. This is what fixes the
 * "first add from empty cart" case: previously the template was captured only from
 * the live element which never rendered when cart == empty.
 *
 * Observer: watches the whole #shtCartDrawer subtree (not just .js-cart-drawer-footer,
 * which doesn't exist while the cart is empty). On every mutation we (a) refresh the
 * cached template if a live element appears, and (b) re-inject into the footer if it
 * exists but has no <kik-cart-trust> child.
 */
(() => {
  if (customElements.get('kik-cart-trust')) return;

  class KikCartTrust extends HTMLElement {
    static template = null;
    static observerBound = false;

    connectedCallback() {
      KikCartTrust.captureTemplate();
      KikCartTrust.ensureObserver();
      KikCartTrust.restoreIfMissing();
    }

    /* Cache the badge markup. Prefer a live <kik-cart-trust> (footer is populated)
       since it's the exact same HTML the live render emits. Fall back to the hidden
       <template id="kik-cart-trust-template"> when the cart is empty and no live
       element exists yet — this is the path that fixes the empty→first-item bug. */
    static captureTemplate() {
      if (KikCartTrust.template) return;
      const live = document.querySelector('.js-cart-drawer-footer kik-cart-trust');
      if (live) {
        KikCartTrust.template = live.outerHTML;
        return;
      }
      const tpl = document.getElementById('kik-cart-trust-template');
      const tplHtml = tpl && tpl.innerHTML.trim();
      if (tplHtml) KikCartTrust.template = tplHtml;
    }

    static ensureObserver() {
      if (KikCartTrust.observerBound) return;
      const drawer = document.getElementById('shtCartDrawer');
      if (!drawer) return;
      KikCartTrust.observerBound = true;
      new MutationObserver(() => {
        KikCartTrust.captureTemplate();
        KikCartTrust.restoreIfMissing();
      }).observe(drawer, { childList: true, subtree: true });
    }

    static restoreIfMissing() {
      const footer = document.querySelector('.js-cart-drawer-footer');
      if (!footer) return;
      if (footer.querySelector('kik-cart-trust')) return;
      if (!KikCartTrust.template) return;

      const anchor = footer.querySelector('.cart-amount') || footer;
      const payments = anchor.querySelector('.product__payment-method');
      if (payments) {
        payments.insertAdjacentHTML('beforebegin', KikCartTrust.template);
      } else {
        anchor.insertAdjacentHTML('beforeend', KikCartTrust.template);
      }
    }
  }

  customElements.define('kik-cart-trust', KikCartTrust);

  /* Bootstrap once on script load — the hidden <template> is present on first paint
     even when the cart is empty, so we can cache it immediately, before any AJAX
     mutation. Also wire the observer now so the empty→populated transition is
     caught (no live <kik-cart-trust> exists yet to trigger connectedCallback). */
  const bootstrap = () => {
    KikCartTrust.captureTemplate();
    KikCartTrust.ensureObserver();
    KikCartTrust.restoreIfMissing();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
})();
